// "Add account" (/app/accounts/new): a page in two steps, centered like the rest of the app.
// 1. Where do you trade? (the platform). 2. How should your trades come in? Live sync
// (TopstepX: tick its accounts, each one's plan and rules detected from its name), a trade
// history file, or by hand (with the account's type, size and rules inline).
import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { ProLock } from './Plans';
import { PRESETS, firmsForPlatform } from './balance';
import { LogoBadge } from './Logos';
import { FirmSearch, PlanPicker } from './FirmPicker';
import { useI18n } from './i18n';
import { usd } from './pnl';
import { TEMPLATE_HREF } from './csvTemplate';
import { IBKR_GUIDE_PATH } from './ibkrGuideData';

// `platform` is what's saved on the account; `firm` the prop firm whose plans load by default.
const FUTURES = [
  { id: 'TopstepX', label: 'TopstepX', note: 'Topstep plans · 50K / 100K / 150K', badge: 'TX', className: 'pf-topstepx', platform: 'TopstepX', firm: 'Topstep', sync: true },
  { id: 'Tradovate', label: 'Tradovate', note: 'Used by many prop firms, like Apex and Take Profit Trader', badge: 'TV', className: 'pf-tradovate', platform: 'Tradovate', sync: 'soon' },
  { id: 'NinjaTrader', label: 'NinjaTrader', note: 'Web platform · Orders export', badge: 'NT', className: 'pf-ninjatrader', platform: 'NinjaTrader' },
  { id: 'IBKR', label: 'Interactive Brokers', note: 'Stocks, options and futures · Flex Web Service', badge: 'IB', className: 'pf-ibkr', platform: 'Other', sync: 'ibkr', personal: true },
  { id: 'Tradesea', label: 'Tradesea', note: 'Used by prop firms like Lucid and Tradeify · Orders export', badge: 'TS', className: 'pf-tradesea', platform: 'Tradesea' },
  { id: 'Rithmic', label: 'Rithmic', note: 'R | Trader Pro · with our CSV template', badge: 'R', className: 'pf-rithmic', platform: 'Rithmic', template: true },
];
const STOCKS = [
  { id: 'Webull', label: 'Webull', badge: 'WB', className: 'pf-webull', platform: 'Other', personal: true, stocks: true },
  { id: 'Schwab', label: 'Charles Schwab', note: 'incl. thinkorswim', badge: 'CS', className: 'pf-schwab', platform: 'Other', personal: true, stocks: true, sync: 'schwab' },
  { id: 'Robinhood', label: 'Robinhood', badge: 'RH', className: 'pf-robinhood', platform: 'Other', personal: true, stocks: true },
];
const OTHER = { id: 'Other', label: 'Other', name: 'My', badge: '•', className: 'pf-other', platform: 'Other', template: true };

const RULES = [
  { key: 'maxLossLimit', label: 'Max loss room', unit: '$' },
  { key: 'dailyLossLimit', label: 'Daily loss', unit: '$' },
  { key: 'profitTarget', label: 'Profit target', unit: '$' },
  { key: 'consistencyLimit', label: 'Consistency', unit: '%' },
];
const RULE_KEYS = ['profitTarget', 'maxLossLimit', 'dailyLossLimit', 'consistencyLimit', 'drawdownType', 'trailLockOffset', 'consistencyBase', 'payoutWinningDays', 'payoutMinDayProfit'];
const BACKFILL = [['7', '7d'], ['30', '30d'], ['90', '90d'], ['all', 'All']];

const k = (n) => (n >= 1000 ? `${Math.round(n / 1000)}K` : String(n));
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
const money = (n) => usd.format(Number(n)).replace(/\.00$/, '');

// A TopstepX account's plan, from its name: 150KTC-… is a 150K Trading Combine, EXPRESS-… an
// Express Funded Account, PRAC-… a practice account.
function detectPlan(name) {
  const n = String(name).toUpperCase();
  const m = n.match(/(\d{2,3})K/);
  const size = m ? Number(m[1]) * 1000 : null;
  if (/PRAC/.test(n)) return { type: 'Personal', size: size || null, label: 'Practice account', preset: null };
  const type = /LFA|LIVE/.test(n) ? 'Live' : /EXPRESS|XFA|FUNDED/.test(n) ? 'Funded' : 'Evaluation';
  const preset = size ? PRESETS.find((p) => p.firm === 'Topstep' && p.type === type && p.size === size && (/STATIC/.test(n) ? /static/i.test(p.label) : !/static/i.test(p.label))) : null;
  const label = size ? `${k(size)} ${type === 'Live' ? 'Live Funded' : type === 'Funded' ? 'Express Funded' : 'Trading Combine'}` : type;
  // …-DLL-… accounts come with a daily loss limit.
  const dll = /DLL/.test(n) && DAILY_LOSS[size] ? DAILY_LOSS[size] : null;
  return { type, size, label, preset, dll };
}
const DAILY_LOSS = { 50000: 1000, 100000: 2000, 150000: 3000 };
const rulesFrom = (preset, dll) => ({
  ...(preset ? Object.fromEntries(RULE_KEYS.filter((key) => preset[key] != null).map((key) => [key, preset[key]])) : {}),
  ...(dll ? { dailyLossLimit: dll } : {}),
});
export { detectPlan, rulesFrom };
const countRules = (r) => ['maxLossLimit', 'dailyLossLimit', 'profitTarget', 'consistencyLimit'].filter((key) => r[key]).length;

// The account card as it will look on the Accounts page.
function PreviewCard({ badge, className, logo, name, sub, chip, rules, personal, size, footer }) {
  const { t } = useI18n();
  return (
    <div className="nw-preview-card">
      <div className="nw-preview-head">
        <LogoBadge logo={logo} size="pf-md" className={className} text={badge} />
        <span className="nw-preview-name"><strong>{name}</strong><small>{sub}</small></span>
        {chip && <span className="guard-chip hot">{chip}</span>}
      </div>
      {personal ? (
        <div className="nw-preview-personal">
          <p className="guard-label">{size ? t('Balance') : t('Net P&L')}</p>
          <strong>{size ? money(size) : '$0'}</strong>
          <svg className="spark up" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true"><polyline points="0,22 20,20 40,21 60,16 80,17 100,12" vectorEffect="non-scaling-stroke" /></svg>
          <small>{t('No firm rules: P&L only')}</small>
        </div>
      ) : (
        <div className="nw-preview-meters">
          {RULES.map((r) => {
            const v = rules[r.key];
            const value = !v ? '—' : r.key === 'maxLossLimit' ? `${money(v)} / ${k(v)}` : r.unit === '%' ? `— / ${v}%` : `$0 / ${k(v)}`;
            return (
              <div key={r.key} className="nw-preview-meter">
                <div><span>{t(r.label)}</span><strong>{value}</strong></div>
                <div className={`acct-meter-bar ${r.key === 'maxLossLimit' ? 'tone-good' : 'tone-target'}`}><span style={{ width: r.key === 'maxLossLimit' && v ? '100%' : '0%' }} /></div>
              </div>
            );
          })}
        </div>
      )}
      {footer && <div className="nw-preview-foot">{footer}</div>}
    </div>
  );
}

export default function AddAccountWizard({ accounts, onCreated, onImportFile, onAddManual, onSynced, onClose, onSessionExpired }) {
  const { t, lang } = useI18n();
  const [step, setStep] = useState(1);
  const [query, setQuery] = useState('');
  const [choice, setChoice] = useState(null);
  const [method, setMethod] = useState('');
  // Account details, for a file or trades by hand.
  const [type, setType] = useState('Evaluation');
  const [firm, setFirm] = useState('');
  const [presetId, setPresetId] = useState('');
  const [size, setSize] = useState('');
  const [rules, setRules] = useState({});
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [file, setFile] = useState(null);
  const fileInput = useRef(null);
  // Live sync.
  const [sync, setSync] = useState(null);
  const [loadingSync, setLoadingSync] = useState(false);
  const [creds, setCreds] = useState({ userName: '', apiKey: '' });
  const [picks, setPicks] = useState({});
  const [backfill, setBackfill] = useState('30');
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const box = useRef(null);
  const search = useRef(null);
  const nextRef = useRef(null);

  const syncAvailable = Boolean(sync && sync.available);
  // Interactive Brokers (ibkr.js): its own connection, accounts and links.
  const [ib, setIb] = useState(null);
  const [ibCreds, setIbCreds] = useState({ token: '', queryId: '' });
  const [ibPicks, setIbPicks] = useState({});
  // Charles Schwab (schwab.js): log in on Schwab's page, then tick the accounts. Admin only for now.
  const [sc, setSc] = useState(null);
  const [scPicks, setScPicks] = useState({});
  // Live sync is Pro: on the Free plan it shows with a Pro note instead (plans.js).
  const syncLocked = (c) => Boolean(c && (c.sync === true ? sync && sync.locked : c.sync === 'ibkr' ? ib && ib.locked : false));
  const canSync = (c) => Boolean(c && !syncLocked(c) && (c.sync === true ? syncAvailable : c.sync === 'ibkr' ? ib && ib.available : c.sync === 'schwab' ? sc && sc.available : false));
  const personal = type === 'Personal';
  const plans = PRESETS.filter((p) => p.firm === firm && p.type === type).sort((a, b) => a.size - b.size);

  useEffect(() => {
    box.current?.focus();
    api('/topstepx?lite=1').then(setSync).catch(() => setSync({ available: false }));
    api('/ibkr').then((s) => { setIb(s); preselectIb(s); }).catch(() => setIb({ available: false }));
    api('/schwab').then((s) => { setSc(s); preselectSc(s); }).catch(() => setSc({ available: false }));
    // Back from Schwab's login page (schwab.js /schwab/callback): straight to the Schwab step.
    const back = new URLSearchParams(window.location.search).get('schwab');
    if (back) {
      const schwab = STOCKS.find((p) => p.sync === 'schwab');
      pickPlatform(schwab);
      setMethod(back === 'unavailable' ? 'file' : 'sync');
      setStep(2);
      if (back === 'denied') setError(t('Schwab didn’t give access, so nothing was connected. Click Log in with Schwab to try again.'));
      else if (back === 'failed') setError(t('Connecting to Schwab didn’t work. Please try again in a minute.'));
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // While IBKR builds the first report (a few minutes at most), check back every 10 seconds.
  const ibBuilding = Boolean(ib && ib.building);
  useEffect(() => {
    if (!ibBuilding) return undefined;
    const timer = setInterval(() => {
      api('/ibkr').then((s) => { setIb(s); if (!s.building) preselectIb(s); }).catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [ibBuilding]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes, / jumps to search, Enter moves on.
  useEffect(() => {
    function onKey(e) {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName);
      if (e.key === 'Escape') onClose();
      else if (e.key === '/' && step === 1 && !typing) { e.preventDefault(); search.current?.focus(); }
      else if (e.key === 'Enter' && !e.defaultPrevented && e.target.tagName !== 'BUTTON' && e.target.tagName !== 'TEXTAREA' && !(typing && e.target.form)) {
        if (nextRef.current && !nextRef.current.disabled) { e.preventDefault(); nextRef.current.click(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, step]);

  const fail = (err) => { if (err.status === 401) return onSessionExpired(); setError(err.message); };

  function suggestName(nextFirm, nextSize, nextType, p = choice) {
    if (nameTouched) return;
    const base = nextFirm || (p && (p.name || p.label));
    setName(nextType === 'Personal' || !nextSize ? t('{platform} account', { platform: base }) : `${base} ${k(nextSize)}`);
  }

  function pickPlatform(p) {
    setChoice(p);
    const nextType = p.personal ? 'Personal' : type === 'Personal' ? 'Evaluation' : type;
    setType(nextType);
    setFirm(p.firm || '');
    setPresetId('');
    setRules({});
    setSize('');
    suggestName(p.firm || '', 0, nextType, p);
    setError('');
  }

  function applyPlan(p) {
    setPresetId(p.id);
    setSize(String(p.size));
    setRules(rulesFrom(p));
    suggestName(p.firm, p.size, type);
  }

  function changeType(next) {
    setType(next);
    setPresetId('');
    setRules({});
    suggestName(next === 'Personal' ? '' : firm, Number(size), next);
  }

  function goToImport() {
    setMethod(canSync(choice) ? 'sync' : 'file');
    setError('');
    setStep(2);
  }

  // Live sync: the connection and the TopstepX accounts, loaded when it's chosen.
  useEffect(() => {
    if (step !== 2 || method !== 'sync' || (sync && sync.accounts) || loadingSync) return;
    setLoadingSync(true);
    api('/topstepx').then((s) => { setSync(s); preselect(s); }).catch(fail).finally(() => setLoadingSync(false));
  }, [step, method]); // eslint-disable-line react-hooks/exhaustive-deps

  // IBKR accounts already synced, and the ones ticked.
  const ibLinked = new Set(((ib && ib.links) || []).filter((l) => l.accountId && accounts.some((a) => a._id === l.accountId)).map((l) => l.ibAccount));
  const chosenIb = ((ib && ib.accounts) || []).map((a) => a.id).filter((id) => ibPicks[id] && !ibLinked.has(id));
  function preselectIb(s) {
    const next = {};
    for (const a of (s && s.accounts) || []) next[a.id] = true;
    setIbPicks(next);
  }
  // Schwab accounts already synced, and the ones ticked.
  const scLinked = new Set(((sc && sc.links) || []).filter((l) => l.accountId && accounts.some((a) => a._id === l.accountId)).map((l) => l.schwabAccount));
  const chosenSc = ((sc && sc.accounts) || []).map((a) => a.id).filter((id) => scPicks[id] && !scLinked.has(id));
  function preselectSc(s) {
    const next = {};
    for (const a of (s && s.accounts) || []) next[a.id] = true;
    setScPicks(next);
  }

  async function connectIb(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const s = await api('/ibkr', { method: 'POST', body: ibCreds });
      setIb(s);
      preselectIb(s);
      setIbCreds({ token: '', queryId: '' });
    } catch (err) { fail(err); } finally { setBusy(false); }
  }

  // Ask IBKR for a fresh report with the saved token (after a failed report or a changed Flex Query).
  async function refreshIb() {
    setBusy(true);
    setError('');
    try { setIb(await api('/ibkr/refresh', { method: 'POST' })); } catch (err) { fail(err); } finally { setBusy(false); }
  }

  // TopstepX accounts already synced into one of the trader's accounts (a deleted account frees its TopstepX account).
  const accountIds = new Set(accounts.map((a) => a._id));
  const linkedIds = new Set(((sync && sync.links) || []).filter((l) => l.accountId && accountIds.has(l.accountId)).map((l) => l.tsxAccountId));
  function preselect(s) {
    const linked = new Set(((s && s.links) || []).filter((l) => l.accountId && accountIds.has(l.accountId)).map((l) => l.tsxAccountId));
    const next = {};
    (s.accounts || []).forEach((a) => { if (!linked.has(a.id)) next[a.id] = !/PRAC/i.test(a.name) || (s.accounts || []).length === 1; });
    setPicks(next);
  }

  async function connect(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const s = await api('/topstepx', { method: 'POST', body: creds });
      setSync(s);
      preselect(s);
      setCreds({ userName: '', apiKey: '' });
    } catch (err) { fail(err); } finally { setBusy(false); }
  }

  const chosenTsx = ((sync && sync.accounts) || []).filter((a) => picks[a.id] && !linkedIds.has(a.id));
  const since = backfill === 'all' ? '2020-01-01' : daysAgo(Number(backfill));
  const uniqueName = (base, taken) => {
    let n = base;
    for (let i = 2; taken.has(n.toLowerCase()); i += 1) n = `${base} #${i}`;
    taken.add(n.toLowerCase());
    return n;
  };

  async function startTracking() {
    setError('');
    if (method === 'sync' && choice.sync === 'schwab') {
      if (!chosenSc.length) return setError(t('Tick at least one Schwab account to add.'));
      setBusy(true);
      try {
        const keep = (sc.links || []).filter((l) => l.accountId && accountIds.has(l.accountId)).map((l) => ({ schwabAccount: l.schwabAccount, accountId: l.accountId, since: l.since }));
        const add = chosenSc.map((id) => {
          const existing = accounts.find((x) => !x.archived && x.externalId === `schwab-${id}`);
          return { schwabAccount: id, accountId: existing ? existing._id : 'new', since, name: existing ? existing.name : `Schwab ${id.slice(-4)}` };
        });
        await api('/schwab/links', { method: 'PUT', body: { links: [...keep, ...add.map(({ name: _n, ...l }) => l)] } });
        const r = await api('/schwab/sync', { method: 'POST' });
        setDone({ names: add.map((x) => x.name), trades: r.result ? r.result.added || 0 : 0, platform: 'Charles Schwab' });
        onSynced();
      } catch (err) { fail(err); } finally { setBusy(false); }
      return;
    }
    if (method === 'sync' && choice.sync === 'ibkr') {
      if (!chosenIb.length) return setError(t('Tick at least one IBKR account to add.'));
      setBusy(true);
      try {
        const keep = (ib.links || []).filter((l) => l.accountId && accountIds.has(l.accountId)).map((l) => ({ ibAccount: l.ibAccount, accountId: l.accountId, since: l.since }));
        const add = chosenIb.map((id) => {
          const existing = accounts.find((x) => !x.archived && x.externalId === id);
          return { ibAccount: id, accountId: existing ? existing._id : 'new', since, name: existing ? existing.name : `IBKR ${id.slice(-4)}` };
        });
        await api('/ibkr/links', { method: 'PUT', body: { links: [...keep, ...add.map(({ name: _n, ...l }) => l)] } });
        const r = await api('/ibkr/sync', { method: 'POST' });
        setDone({ names: add.map((x) => x.name), trades: r.result ? r.result.added || 0 : 0, platform: 'Interactive Brokers' });
        onSynced();
      } catch (err) { fail(err); } finally { setBusy(false); }
      return;
    }
    if (method === 'sync') {
      if (!chosenTsx.length) return setError(t('Tick at least one TopstepX account to add.'));
      setBusy(true);
      try {
        const taken = new Set(accounts.map((a) => a.name.toLowerCase()));
        const added = [];
        for (const a of chosenTsx) {
          const plan = detectPlan(a.name);
          // An account already made for this TopstepX account (e.g. from an earlier sync or a file
          // import) is reused, with its plan's rules if it has none yet, instead of a duplicate.
          const existing = accounts.find((x) => !x.archived && (x.externalId === a.name || x.name === a.name));
          if (existing) {
            const noRules = !(existing.maxLossLimit || existing.profitTarget || existing.dailyLossLimit || existing.consistencyLimit);
            const rules = plan.type !== 'Personal' ? rulesFrom(plan.preset, plan.dll) : {};
            if (noRules && Object.keys(rules).length) {
              await api(`/accounts/${existing._id}`, { method: 'PATCH', body: { type: existing.type || plan.type, ...(existing.size ? {} : plan.size ? { size: plan.size } : {}), ...rules } });
            }
            added.push({ tsxAccountId: a.id, tsxName: a.name, accountId: existing._id, since, name: existing.name });
            continue;
          }
          const base = plan.preset ? `Topstep ${k(plan.size)}${plan.type === 'Funded' ? ' XFA' : ''}` : a.name;
          const created = await api('/accounts', { method: 'POST', body: {
            name: uniqueName(base, taken).slice(0, 60), platform: 'TopstepX', type: plan.type, externalId: a.name,
            ...(plan.size ? { size: plan.size } : {}), ...(plan.type !== 'Personal' ? rulesFrom(plan.preset, plan.dll) : {}),
          } });
          onCreated(created);
          added.push({ tsxAccountId: a.id, tsxName: a.name, accountId: created._id, since, name: created.name });
        }
        const keep = (sync.links || []).filter((l) => l.accountId && accountIds.has(l.accountId)).map((l) => ({ tsxAccountId: l.tsxAccountId, tsxName: l.tsxName, accountId: l.accountId, since: l.since }));
        await api('/topstepx/links', { method: 'PUT', body: { links: [...keep, ...added.map(({ name: _n, ...l }) => l)] } });
        const r = await api('/topstepx/sync', { method: 'POST' });
        setDone({ names: added.map((x) => x.name), trades: r.result ? r.result.added || 0 : 0 });
        onSynced();
      } catch (err) { fail(err); } finally { setBusy(false); }
      return;
    }
    if (method === 'file' && !file) return setError(t('Choose the file with your trades.'));
    setBusy(true);
    try {
      const body = { name: name.trim() || t('My account'), platform: choice.platform, type, ...(size ? { size: Number(size) } : {}) };
      if (!personal) for (const [key, v] of Object.entries(rules)) if (v !== '' && v != null) body[key] = ['drawdownType', 'consistencyBase'].includes(key) ? v : Number(v);
      if (!body.maxLossLimit) delete body.drawdownType;
      if (!body.consistencyLimit) delete body.consistencyBase;
      const created = await api('/accounts', { method: 'POST', body });
      onCreated(created);
      if (method === 'file') onImportFile(file, created);
      else onAddManual(created);
    } catch (err) { fail(err); setBusy(false); }
  }

  // What the preview on the right shows.
  const syncPlan = method === 'sync' && choice && choice.sync === true && step === 2 && chosenTsx[0] ? detectPlan(chosenTsx[0].name) : null;
  const preview = !choice ? null : syncPlan ? {
    name: syncPlan.preset ? `Topstep ${k(syncPlan.size)}${syncPlan.type === 'Funded' ? ' XFA' : ''}` : chosenTsx[0].name,
    sub: chosenTsx[0].name, chip: t(syncPlan.type), rules: rulesFrom(syncPlan.preset, syncPlan.dll), personal: syncPlan.type === 'Personal', size: syncPlan.size,
  } : {
    name: name || t('{platform} account', { platform: choice.name || choice.label }),
    sub: step === 1 && !choice.personal ? t('Details load in step 2') : [t(choice.label), t(type), size && k(Number(size))].filter(Boolean).join(' · '),
    chip: choice.stocks ? t('Stocks') : choice.personal || personal ? t('Personal') : step === 1 ? t('Prop firm') : t(type), rules, personal: choice.personal || personal, size,
  };

  const match = (p) => !query || `${p.label} ${p.note || ''}`.toLowerCase().includes(query.toLowerCase());
  const futures = FUTURES.filter(match);
  const stocks = STOCKS.filter(match);
  const showOther = match(OTHER) || (!futures.length && !stocks.length);

  const detailsForm = () => (
    <div className="nw-inline">
      <label className="nw-label">{t('Nickname')}
        <input value={name} onChange={(e) => { setName(e.target.value); setNameTouched(true); }} maxLength={60} />
      </label>
      {!choice.personal && (
        <div className="nw-segment" role="radiogroup" aria-label={t('Account type')}>
          {['Evaluation', 'Funded', 'Live', 'Personal'].map((x) => (
            <label key={x} className={type === x ? 'on' : ''}><input type="radio" name="type" checked={type === x} onChange={() => changeType(x)} />{t(x)}</label>
          ))}
        </div>
      )}
      {!personal && !choice.firm && (
        <div className="nw-label">{t('Prop firm')}
          <FirmSearch value={firm} firms={firmsForPlatform(choice && choice.platform)} onChange={(f) => { setFirm(f); setPresetId(''); setRules({}); suggestName(f, Number(size), type); }} />
        </div>
      )}
      {!personal && plans.length > 0 ? (
        <PlanPicker key={`${firm}-${type}`} firm={firm} type={type} presetId={presetId} onPick={applyPlan} />
      ) : (
        <label className="nw-label">{t('Starting balance ($)')}
          <input type="number" min="0" step="any" value={size} onChange={(e) => { setSize(e.target.value); suggestName(firm, Number(e.target.value), type); }} placeholder={t('e.g. 50000')} />
        </label>
      )}
      {!personal && (
        <>
          <button type="button" className="link-button nw-rules-toggle" onClick={() => setRulesOpen((v) => !v)}>
            {rulesOpen ? t('Hide rules') : t(countRules(rules) ? 'Edit rules ({n} loaded)' : 'Add rules', { n: countRules(rules) })}
          </button>
          {rulesOpen && RULES.filter((r) => (type !== 'Funded' && type !== 'Live') || r.key !== 'profitTarget').map((r) => (
            <label className="nw-rule-edit" key={r.key}>
              <span>{t({ maxLossLimit: 'Max loss limit', dailyLossLimit: 'Daily loss limit', profitTarget: 'Profit target', consistencyLimit: 'Consistency rule' }[r.key])}</span>
              <span className="nw-money">
                {r.unit === '$' && <i>$</i>}
                <input type="number" min="0" step="any" value={rules[r.key] || ''} onChange={(e) => setRules((m) => ({ ...m, [r.key]: e.target.value }))} placeholder="—" />
                {r.unit === '%' && <i>%</i>}
              </span>
            </label>
          ))}
        </>
      )}
    </div>
  );


  const title = done ? t('You’re tracking') : step === 1 ? t('Where do you trade?') : t('How should your trades come in?');
  const lead = done
    ? t('{names} synced with {platform}: {n} trades brought in. New trades arrive by themselves.', { names: done.names.join(', '), n: done.trades, platform: done.platform || 'TopstepX' })
    : step === 1 ? t('Pick the platform you place trades on. Prop firm platforms come with the firm’s rules loaded, so your rule meters work from the first trade.')
      : t('Pick how trades reach your journal. Live sync keeps your rule meters current during the session.');

  return (
    <div className="nw-page" ref={box} tabIndex={-1} aria-labelledby="nw-title">
      <div className="routine-header nw-page-head">
        <div>
          <h1 className="routine-title" id="nw-title">{title}</h1>
          <p className="subtitle">{lead}</p>
        </div>
      </div>
      <ol className="nw-stepper" aria-label={t('Steps')}>
        {[
          { n: 1, title: t('Platform'), sub: choice && step > 1 ? t(choice.label) : t('Where you place trades') },
          { n: 2, title: t('Import'), sub: t('How your trades come in') },
        ].map((s) => (
          <li key={s.n} className={step === s.n && !done ? 'current' : step > s.n || done ? 'done' : ''} aria-current={step === s.n && !done ? 'step' : undefined}>
            <span className="nw-dot">{step > s.n || done ? '✓' : s.n}</span>
            <span><strong>{s.title}</strong><small>{s.sub}</small></span>
          </li>
        ))}
      </ol>
      <div className="nw-main">

          {done ? (
            <div className="nw-form">
              <div className="nw-foot"><span /><button type="button" className="nw-next" onClick={onClose}>{t('Go to my accounts')} &rarr;</button></div>
            </div>
          ) : step === 1 ? (
            <>
              <p className="nw-section">{t('Search')}</p>
              <label className="nw-search">
                <span className="visually-hidden">{t('Search platforms')}</span>
                <input ref={search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder="TopstepX, Tradovate, Schwab…" />
                <kbd aria-hidden="true">/</kbd>
              </label>
              {futures.length > 0 && (
                <>
                  <p className="nw-section"><span>{t('Prop firm & futures')}</span><span className="nw-count">{t('{n} platforms', { n: futures.length })}</span></p>
                  <div className="nw-list" role="radiogroup">
                    {futures.map((p) => (
                      <label key={p.id} className={`nw-option${choice && choice.id === p.id ? ' on' : ''}`}>
                        <input type="radio" name="platform" checked={Boolean(choice && choice.id === p.id)} onChange={() => pickPlatform(p)} />
                        <LogoBadge logo={p.logo || p.id} size="pf-md" className={p.className} text={p.badge} />
                        <span className="nw-option-text"><strong>{p.label}</strong><small>{t(p.note)}</small></span>
                        <span className="nw-tags">
                          {p.firm && <span className="nw-tag hot">◆ {t('Rules auto-loaded')}</span>}
                          {p.sync === true && syncAvailable && <span className="nw-tag live"><i />{t('Live sync')}</span>}
                          {p.sync === 'soon' && <span className="nw-tag">{t('Sync soon')}</span>}
                          <span className="nw-tag box">{p.template ? t('CSV') : t('File')}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              )}
              {stocks.length > 0 && (
                <>
                  <p className="nw-section"><span>{t('Stocks & options')}</span><span className="nw-count">{t('File import')}</span></p>
                  <div className="nw-pills" role="radiogroup">
                    {stocks.map((p) => (
                      <label key={p.id} className={`nw-pill big${choice && choice.id === p.id ? ' on' : ''}`}>
                        <input type="radio" name="platform" className="visually-hidden" checked={Boolean(choice && choice.id === p.id)} onChange={() => pickPlatform(p)} />
                        <LogoBadge logo={p.logo || p.id} size="pf-sm" className={p.className} text={p.badge} />
                        {t(p.label)}{p.note && <small>{t(p.note)}</small>}
                      </label>
                    ))}
                  </div>
                </>
              )}
              {showOther && (
                <label className={`nw-other${choice && choice.id === 'Other' ? ' on' : ''}`}>
                  <input type="radio" name="platform" className="visually-hidden" checked={Boolean(choice && choice.id === 'Other')} onChange={() => pickPlatform(OTHER)} />
                  <span className="pf-badge pf-md pf-other" aria-hidden="true">•</span>
                  <span><strong>{t('Other')}</strong> — {t('any broker, with our CSV template')}</span>
                  <a href={TEMPLATE_HREF} download="trades-template.csv" onClick={(e) => e.stopPropagation()}>{t('Download template')} ↓</a>
                </label>
              )}
              <div className="nw-foot">
                <button type="button" className="nw-back" onClick={onClose}>{t('Cancel')}</button>
                <span className="nw-foot-right">
                  <span className="nw-enter">{t('Press')} <kbd>Enter ↵</kbd></span>
                  <button type="button" ref={nextRef} className="nw-next" disabled={!choice} onClick={goToImport}>{t('Next: import')} &rarr;</button>
                </span>
              </div>
            </>
          ) : (
            <>
              <p className="nw-section">{t('Import method')}</p>
              <div className="nw-methods" role="radiogroup">
                {choice.sync && (choice.sync !== 'schwab' || canSync(choice)) && (
                  <div className={`nw-method${method === 'sync' ? ' on' : ''}${canSync(choice) ? '' : ' off'}${choice.sync === 'schwab' ? ' admin-preview-box' : ''}`}>
                    <label className="nw-method-head">
                      <input type="radio" name="method" disabled={!canSync(choice)} checked={method === 'sync'} onChange={() => setMethod('sync')} />
                      <span><strong>{t('Live sync with {platform}', { platform: choice.label })} <em className={canSync(choice) ? '' : 'muted'}>{canSync(choice) ? `◆ ${t(choice.sync === 'ibkr' ? 'Beta · No more file exports' : choice.sync === 'schwab' ? 'Admin only · No more file exports' : 'Best for rule alerts')}` : syncLocked(choice) ? t('Pro') : t('Coming soon')}</em></strong>
                        <small>{choice.sync === 'ibkr' ? t('We download your IBKR trade report automatically, a few times a day.') : choice.sync === 'schwab' ? t('We read your Schwab trades automatically, thinkorswim included.') : t('We pull new trades automatically, so your rule meters stay current.')}</small></span>
                      <span className="nw-method-meta">{choice.sync === 'ibkr' || choice.sync === 'schwab' ? t('Every hour') : t('Every 15 min')}</span>
                    </label>
                    {syncLocked(choice) && <ProLock compact text={t('Live sync is part of Pro. Import from a file below, free.')} />}
                    {method === 'sync' && choice.sync === 'schwab' && (
                      <div className="nw-sync">
                        {!sc ? <p className="nw-hint">{t('Loading…')}</p> : !sc.connected ? (
                          <div className="nw-connect">
                            <ol className="nw-howto">
                              <li>{t('Click Log in with Schwab. Schwab’s own login page opens.')}</li>
                              <li>{t('Log in with your Schwab username and password, tick the accounts to share and click Allow.')}</li>
                              <li>{t('Schwab sends you back here to pick which accounts go into your journal.')}</li>
                            </ol>
                            <div className="nw-connect-foot">
                              <small>{t('Read-only: we only read your trades and never place orders. Schwab asks you to log in again every 7 days.')}</small>
                              <a className="nw-back small" href="/api/schwab/connect">{t('Log in with Schwab')}</a>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="nw-sync-head">
                              <span className="acct-pill pill-synced"><i aria-hidden="true" />{t('Connected')}</span>
                              <span>{t('{n} Schwab accounts found', { n: (sc.accounts || []).length })}</span>
                              <a className="link-button" href="/api/schwab/connect">{t('Reconnect')}</a>
                            </div>
                            {sc.lastError && <p className="error" style={{ margin: 14 }}>{t(sc.lastError)}</p>}
                            {!(sc.accounts || []).length && <p className="nw-hint" style={{ padding: 14 }}>{t('Schwab didn’t share any accounts. Click Reconnect and tick your accounts on Schwab’s page.')}</p>}
                            {(sc.accounts || []).map((a) => {
                              const already = scLinked.has(a.id);
                              const match = !already && accounts.find((x) => !x.archived && x.externalId === `schwab-${a.id}`);
                              return (
                                <label key={a.id} className={`nw-tsx${already ? ' added' : ''}${scPicks[a.id] && !already ? ' on' : ''}`}>
                                  <input type="checkbox" disabled={already} checked={already || Boolean(scPicks[a.id])} onChange={(e) => setScPicks((m) => ({ ...m, [a.id]: e.target.checked }))} />
                                  <span className="nw-tsx-text"><strong>{a.name}</strong>
                                    <small>{already ? t('Already tracked in your journal') : match ? t('Goes into your existing account {name}', { name: match.name }) : t('A new account named {name}', { name: `Schwab ${a.id.slice(-4)}` })}</small></span>
                                  {already && <span className="nw-tag box">{t('Already added')}</span>}
                                </label>
                              );
                            })}
                            <div className="nw-backfill">
                              <span>{t('Backfill history')}</span>
                              {BACKFILL.map(([id, label]) => (
                                <button type="button" key={id} className={backfill === id ? 'on' : ''} onClick={() => setBackfill(id)}>{t(id === 'all' ? '1y' : label)}</button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {method === 'sync' && choice.sync === 'ibkr' && (
                      <div className="nw-sync">
                        {!ib ? <p className="nw-hint">{t('Loading…')}</p> : !ib.connected ? (
                          <form onSubmit={connectIb} className="nw-connect">
                            <ol className="nw-howto">
                              <li>{t('In IBKR’s Client Portal, open Performance & Reports → Flex Queries and click + next to Activity Flex Query.')}</li>
                              <li>{t('Give it a name, click Trades, tick Executions, click Select All under the fields, then Save.')}</li>
                              <li>{t('For swing trades, also click Open Positions, tick Summary, click Select All, then Save.')}</li>
                              <li>{t('Under Delivery Configuration choose Format XML and Period Last 365 Calendar Days. Leave the date and time formats as they are. Save the query.')}</li>
                              <li>{t('Copy the Query ID shown next to your new query.')}</li>
                              <li>{t('Click the gear next to Flex Web Service Configuration, turn the service on and generate a token (pick the longest expiry).')}</li>
                              <li>{t('Paste the token and your query’s ID number here.')}</li>
                            </ol>
                            <p className="nw-hint"><a href={lang === 'es' ? `/es${IBKR_GUIDE_PATH}` : IBKR_GUIDE_PATH} target="_blank" rel="noopener noreferrer">{t('Open the step-by-step guide with pictures')} &rarr;</a></p>
                            <p className="nw-hint">{t('Beta: IBKR sync is new. If a trade or its P&L looks off, tell us through Help and we’ll fix it fast.')}</p>
                            <div className="nw-row">
                              <label className="nw-label">{t('Flex Web Service token')}<input type="password" value={ibCreds.token} onChange={(e) => setIbCreds({ ...ibCreds, token: e.target.value })} autoComplete="off" required /></label>
                              <label className="nw-label">{t('Query ID')}<input inputMode="numeric" value={ibCreds.queryId} onChange={(e) => setIbCreds({ ...ibCreds, queryId: e.target.value.replace(/\D/g, '') })} autoComplete="off" required /></label>
                            </div>
                            <div className="nw-connect-foot">
                              <small>{t('Read-only: the token can only download reports. It’s stored encrypted.')}</small>
                              <button type="submit" className="nw-back small" disabled={busy}>{busy ? t('Checking with IBKR…') : t('Connect')}</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="nw-sync-head">
                              <span className="acct-pill pill-synced"><i aria-hidden="true" />{t('Connected')}</span>
                              <span>{ib.building ? t('Flex Query {id}', { id: ib.queryId }) : t('Flex Query {id} · {n} accounts found', { id: ib.queryId, n: (ib.accounts || []).length })}</span>
                            </div>
                            {ib.building && <p className="nw-hint" style={{ padding: 14 }}>{t('Your token and Query ID work. IBKR is now building your report, which can take a few minutes the first time. Your accounts will show here when it’s ready: you can keep this window open or come back later.')}</p>}
                            {!ib.building && ib.lastError && !(ib.accounts || []).length && <p className="error" style={{ margin: 14 }}>{t(ib.lastError)}</p>}
                            {!ib.building && !(ib.accounts || []).length && <p style={{ margin: '0 14px 14px' }}><button type="button" className="nw-back small" onClick={refreshIb} disabled={busy}>{busy ? t('Checking with IBKR…') : t('Ask IBKR again')}</button></p>}
                            {!ib.building && !ib.lastError && !(ib.accounts || []).length && <p className="nw-hint" style={{ padding: 14 }}>{t('The report has no trades yet. Check that the Flex Query includes the Trades section, then try again.')}</p>}
                            {(ib.accounts || []).map((a) => {
                              const already = ibLinked.has(a.id);
                              const match = !already && accounts.find((x) => !x.archived && x.externalId === a.id);
                              return (
                                <label key={a.id} className={`nw-tsx${already ? ' added' : ''}${ibPicks[a.id] && !already ? ' on' : ''}`}>
                                  <input type="checkbox" disabled={already} checked={already || Boolean(ibPicks[a.id])} onChange={(e) => setIbPicks((m) => ({ ...m, [a.id]: e.target.checked }))} />
                                  <span className="nw-tsx-text"><strong>{a.id}</strong>
                                    <small>{already ? t('Already tracked in your journal') : match ? t('Goes into your existing account {name}', { name: match.name }) : t('A new account named {name}', { name: `IBKR ${a.id.slice(-4)}` })}</small></span>
                                  {already && <span className="nw-tag box">{t('Already added')}</span>}
                                </label>
                              );
                            })}
                            <div className="nw-backfill">
                              <span>{t('Backfill history')}</span>
                              {BACKFILL.map(([id, label]) => (
                                <button type="button" key={id} className={backfill === id ? 'on' : ''} onClick={() => setBackfill(id)}>{t(label)}</button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {method === 'sync' && choice.sync === true && (
                      <div className="nw-sync">
                        {loadingSync || !sync || (sync.connected && !sync.accounts) ? <p className="nw-hint">{t('Loading your TopstepX accounts…')}</p> : !sync.connected ? (
                          <form onSubmit={connect} className="nw-connect">
                            <p className="nw-hint">{t('Connect once with TopstepX API Access (half price with the code topstep). In TopstepX: Settings → API → Add API Key.')}</p>
                            <div className="nw-row">
                              <label className="nw-label">{t('TopstepX username')}<input value={creds.userName} onChange={(e) => setCreds({ ...creds, userName: e.target.value })} autoComplete="off" required /></label>
                              <label className="nw-label">{t('API key')}<input type="password" value={creds.apiKey} onChange={(e) => setCreds({ ...creds, apiKey: e.target.value })} autoComplete="off" required /></label>
                            </div>
                            <div className="nw-connect-foot">
                              <small>{t('Read-only: we never place orders. The key is stored encrypted.')}</small>
                              <button type="submit" className="nw-back small" disabled={busy}>{busy ? t('Connecting…') : t('Connect')}</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="nw-sync-head">
                              <span className="acct-pill pill-synced"><i aria-hidden="true" />{t('Connected')}</span>
                              <span>{t('Signed in as {name} · {n} accounts found', { name: sync.userName, n: (sync.accounts || []).length })}</span>
                            </div>
                            {sync.accountsError && <p className="error">{t(sync.accountsError)}</p>}
                            {(sync.accounts || []).map((a) => {
                              const plan = detectPlan(a.name);
                              const already = linkedIds.has(a.id);
                              const match = !already && accounts.find((x) => !x.archived && (x.externalId === a.name || x.name === a.name));
                              const n = countRules(rulesFrom(plan.preset, plan.dll));
                              return (
                                <label key={a.id} className={`nw-tsx${already ? ' added' : ''}${picks[a.id] && !already ? ' on' : ''}`}>
                                  <input type="checkbox" disabled={already} checked={already || Boolean(picks[a.id])} onChange={(e) => setPicks((m) => ({ ...m, [a.id]: e.target.checked }))} />
                                  <span className="nw-tsx-text"><strong>{a.name}</strong>
                                    <small>{already ? t('Already tracked in your journal') : match ? t('Goes into your existing account {name}', { name: match.name }) : plan.preset ? t('Detected: {plan} ({type}) · rules auto-loaded', { plan: t(plan.label), type: t(plan.type) }) : t('Detected: {plan}', { plan: t(plan.label) })}</small></span>
                                  {already ? <span className="nw-tag box">{t('Already added')}</span> : n > 0 ? <span className="nw-tag hot">◆ {t('{n} rules loaded', { n })}</span> : null}
                                </label>
                              );
                            })}
                            <div className="nw-backfill">
                              <span>{t('Backfill history')}</span>
                              {BACKFILL.map(([id, label]) => (
                                <button type="button" key={id} className={backfill === id ? 'on' : ''} onClick={() => setBackfill(id)}>{t(label)}</button>
                              ))}
                              <small>{backfill === 'all' ? t('Every trade on these accounts') : t('Trades since {date}', { date: new Date(`${since}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) })}</small>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className={`nw-method${method === 'file' ? ' on' : ''}`}>
                  <label className="nw-method-head">
                    <input type="radio" name="method" checked={method === 'file'} onChange={() => setMethod('file')} />
                    <span><strong>{t('Upload a trade history file')} <em className="muted">{t('Best for backfilling history')}</em></strong>
                      <small>{choice.template ? t('Fill in our CSV template and upload it.') : t('Export it from {platform} and upload it here.', { platform: choice.label })}</small></span>
                    <span className="nw-method-meta">CSV · XLSX</span>
                  </label>
                  {method === 'file' && (
                    <div className="nw-sync">
                      <button type="button" className={`nw-drop${file ? ' has' : ''}`} onClick={() => fileInput.current.click()}
                        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }}>
                        <strong>{file ? file.name : t('Drop your file here, or click to choose')}</strong>
                        <small>{file ? t('Click to choose a different file') : t('CSV, Excel or text exports')}</small>
                      </button>
                      <input ref={fileInput} type="file" accept=".csv,text/csv,.txt,.tsv,.xls,.xlsx,.pdf" onChange={(e) => setFile(e.target.files[0] || null)} hidden />
                      {detailsForm()}
                    </div>
                  )}
                </div>

                <div className={`nw-method${method === 'manual' ? ' on' : ''}`}>
                  <label className="nw-method-head">
                    <input type="radio" name="method" checked={method === 'manual'} onChange={() => setMethod('manual')} />
                    <span><strong>{t('Enter trades by hand')} <em className="muted">{t('Best for a few trades')}</em></strong>
                      <small>{t('Type trades in one by one, handy for missed days.')}</small></span>
                    <span className="nw-method-meta">{t('Manual')}</span>
                  </label>
                  {method === 'manual' && <div className="nw-sync">{detailsForm()}</div>}
                </div>
              </div>
              {preview && (
                <section className="nw-adding" aria-label={t('You’re adding')}>
                  <p className="nw-section"><span>{t('You’re adding')}</span><span className="nw-count">{t('This card goes live on your Accounts page.')}</span></p>
                  <div className="nw-adding-grid">
                    <PreviewCard badge={choice.badge} className={choice.className} logo={!personal && (firm || choice.firm) ? firm || choice.firm : choice.id} name={preview.name} sub={preview.sub} chip={preview.chip}
                      rules={preview.rules} personal={preview.personal} size={preview.size}
                      footer={<>{method === 'sync' ? <span className="acct-pill pill-synced"><i aria-hidden="true" />{t('Live sync')}</span> : <span className="acct-pill pill-imported"><i aria-hidden="true" />{method === 'manual' ? t('By hand') : t('File import')}</span>}
                        {method === 'sync' && chosenTsx.length > 1 && <small>{t('+{n} more accounts', { n: chosenTsx.length - 1 })}</small>}</>} />
                    <dl className="nw-facts">
                      <div><dt>{t('Platform')}</dt><dd>{t(choice.label)}</dd></div>
                      <div><dt>{t('Nickname')}</dt><dd>{preview.name}</dd></div>
                      <div><dt>{t('Type · size')}</dt><dd>{[preview.chip, preview.size && k(Number(preview.size))].filter(Boolean).join(' · ')}</dd></div>
                      <div><dt>{t('Rules')}</dt><dd>{preview.personal ? t('P&L only') : method === 'sync' ? t('{n} loaded', { n: countRules(preview.rules) }) : (
                        <button type="button" className="link-button" onClick={() => setRulesOpen(true)}>{t('Edit rules')} &rarr;</button>
                      )}</dd></div>
                    </dl>
                  </div>
                </section>
              )}
              {error && <p className="error">{error}</p>}
              <div className="nw-foot">
                <button type="button" className="nw-back" onClick={() => { setError(''); setStep(1); }}>&larr; {t('Back')}</button>
                <span className="nw-foot-right">
                  <span className="nw-enter">{t('Rules editable any time')}</span>
                  <button type="button" ref={nextRef} className="nw-next" disabled={busy || !method || (method === 'sync' && (choice.sync === 'ibkr' ? !chosenIb.length : choice.sync === 'schwab' ? !chosenSc.length : !chosenTsx.length))} onClick={startTracking}>
                    {busy ? t('Please wait…') : `${t('Start tracking')} ▸`}
                  </button>
                </span>
              </div>
            </>
          )}
      </div>
    </div>
  );
}
